import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { api } from "../api.ts";
import { date, moneyShort } from "../format.ts";
import {
  Badge,
  ButtonLink,
  Card,
  Empty,
  ErrorText,
  Loading,
  Page,
  Segmented,
  Stat,
  Strip,
} from "../ui.tsx";

interface HomeData {
  currency: string;
  claimableCents: string;
  soonestDeadline: string | null;
  claimedCents: string;
  recoveredCents: string;
  lostCents: string;
  watching: number;
  todo: { kind: string; title: string; detail: string; href: string; due: string | null }[];
  vendors: {
    vendorId: string;
    name: string;
    owedCents: string;
    feeNeeded: boolean;
    months: { key: string; met: boolean; provisional: boolean; creditCents: string | null }[];
  }[];
  setup: { vendors: number; docs: number; approved: number; claims_sent: number };
  value: {
    recoveredCents: string;
    claimsSent: number;
    hoursSaved: number;
    hoursPerClaim: number;
    missesOnRecord: number;
    vendors: { owesCredits: number; noCredit: number; noTerms: number };
  };
}

const URGENT = new Set([
  "hold_auto_send",
  "notify_now",
  "send_claim",
  "confirm_reply",
  "renewal_notice",
  "check_credit",
]);

export function Home() {
  const [range, setRange] = useState<"12m" | "quarter" | "ytd">("12m");
  const { data, error, isLoading } = useQuery({
    queryKey: ["home", range],
    queryFn: () => api.get<HomeData>(`/home?range=${range}`),
    refetchInterval: 30_000,
  });
  if (isLoading) return <Loading />;
  if (error || !data)
    return (
      <Page help="first-result" title="Home">
        <ErrorText error={error} />
      </Page>
    );
  const cur = data.currency;
  const steps = [
    { done: data.setup.docs > 0, label: "Drop in a vendor contract", href: "/contracts" },
    { done: data.setup.approved > 0, label: "Check and approve its terms", href: "/contracts" },
    { done: data.setup.claims_sent > 0, label: "Send your first claim", href: "/claims" },
  ];
  const setupDone = steps.every((s) => s.done);

  return (
    <Page
      help="first-result"
      title="Home"
      lead="What your vendors owe you, what to claim before the deadline, and what came back."
      actions={
        <ButtonLink to="/contracts" variant="primary">
          Add contracts
        </ButtonLink>
      }
    >
      <div className="flex justify-end">
        <Segmented
          label="Period"
          value={range}
          onChange={setRange}
          options={[
            { id: "12m", label: "Last 12 months" },
            { id: "quarter", label: "This quarter" },
            { id: "ytd", label: "Year to date" },
          ]}
        />
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Stat
          label="Still claimable"
          tone="accent"
          value={moneyShort(data.claimableCents, cur)}
          note={
            data.soonestDeadline
              ? `Soonest deadline ${date(data.soonestDeadline)}`
              : "Nothing waiting to be claimed"
          }
        />
        <Stat
          label="Claimed"
          value={moneyShort(data.claimedCents, cur)}
          note={
            BigInt(data.lostCents) > 0n
              ? `${moneyShort(data.lostCents, cur)} lost to passed deadlines`
              : undefined
          }
        />
        <Stat
          label="Recovered"
          value={moneyShort(data.recoveredCents, cur)}
          note="Confirmed on your invoices"
        />
      </div>

      {!setupDone && (
        <Card title="Get started" description="About three minutes to your first result.">
          <ol className="flex flex-col gap-3">
            {steps.map((s, i) => (
              <li key={s.label} className="flex items-center gap-3 text-sm">
                <span
                  className={`flex size-6 items-center justify-center rounded-full text-xs font-semibold ${s.done ? "bg-ok-soft text-ok" : "bg-surface-2 text-muted"}`}
                >
                  {s.done ? "✓" : i + 1}
                </span>
                {s.done ? (
                  <span className="text-muted line-through">{s.label}</span>
                ) : (
                  <Link className="font-medium text-accent" to={s.href}>
                    {s.label}
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </Card>
      )}

      {data.setup.approved > 0 && <ValueCard v={data.value} currency={cur} />}

      <Card title="Do this now" flush>
        {data.todo.length === 0 ? (
          <Empty title="Nothing to do.">
            recoup is monitoring {data.watching} vendor{data.watching === 1 ? "" : "s"}.
          </Empty>
        ) : (
          <ul className="divide-y divide-line">
            {data.todo.map((t) => (
              <li key={`${t.kind}:${t.href}:${t.title}`}>
                <Link
                  to={t.href}
                  className="flex flex-col gap-1 px-5 py-3.5 hover:bg-surface-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="font-medium">{t.title}</div>
                    <div className="text-sm text-muted">{t.detail}</div>
                  </div>
                  {URGENT.has(t.kind) ? (
                    <Badge tone="warn">Now</Badge>
                  ) : t.kind === "claim_later" ? (
                    <Badge>Later</Badge>
                  ) : (
                    <Badge tone="accent">Setup</Badge>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        title="Vendors, by money owed"
        flush
        actions={<ButtonLink to="/vendors">All vendors</ButtonLink>}
      >
        {data.vendors.length === 0 ? (
          <Empty title="No measured vendors yet.">
            Approve a contract's terms and recoup measures the last 12 months.
          </Empty>
        ) : (
          <ul className="divide-y divide-line">
            {data.vendors.map((v) => (
              <li key={v.vendorId}>
                <Link
                  to={`/vendors/${v.vendorId}`}
                  className="flex flex-col gap-2 px-5 py-3.5 hover:bg-surface-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span className="font-medium">{v.name}</span>
                  <div className="flex items-center gap-4">
                    <Strip months={v.months} />
                    <span className="tabular w-28 text-right font-semibold">
                      {v.feeNeeded && BigInt(v.owedCents) === 0n ? (
                        <Badge tone="warn">Fee needed</Badge>
                      ) : (
                        `${v.feeNeeded ? "At least " : ""}${moneyShort(v.owedCents, cur)}`
                      )}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Page>
  );
}

/** What recoup has done for the organization since it started, in terms it can count. */
function ValueCard({ v, currency }: { v: HomeData["value"]; currency: string }) {
  const terms = v.vendors;
  return (
    <Card
      title="What recoup has done for you"
      description="Since you started, including what to bring to your next renewals."
      actions={<ButtonLink to="/vendors?tab=terms">What each vendor promised</ButtonLink>}
    >
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className="eyebrow">Credits recovered</dt>
          <dd className="font-display text-xl font-bold tabular-nums">
            {moneyShort(v.recoveredCents, currency)}
          </dd>
          <dd className="text-xs text-muted">Confirmed on your invoices</dd>
        </div>
        <div>
          <dt className="eyebrow">Claims prepared and sent</dt>
          <dd className="font-display text-xl font-bold tabular-nums">{v.claimsSent}</dd>
          <dd className="text-xs text-muted">
            About {v.hoursSaved} hours of work, at {v.hoursPerClaim} hours a claim
          </dd>
        </div>
        <div>
          <dt className="eyebrow">Missed months on record</dt>
          <dd className="font-display text-xl font-bold tabular-nums">{v.missesOnRecord}</dd>
          <dd className="text-xs text-muted">Each one dated, with proof, ready for a renewal</dd>
        </div>
        <div>
          <dt className="eyebrow">Vendors whose contracts owe credits</dt>
          <dd className="font-display text-xl font-bold tabular-nums">
            {terms.owesCredits} of {terms.owesCredits + terms.noCredit + terms.noTerms}
          </dd>
          <dd className="text-xs text-muted">
            {terms.noCredit > 0
              ? `${terms.noCredit} promise no credit at all`
              : "Every approved contract owes credits"}
            {terms.noTerms > 0 ? `; ${terms.noTerms} without approved terms` : ""}
          </dd>
        </div>
      </dl>
    </Card>
  );
}
